import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Settings, User, Ban, Bell, Shield, CreditCard, LogOut, ChevronRight, Mail, Sun, Moon, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import BlockedUsersList from '../components/BlockedUsersList';

export default function SettingsPage() {
  const { user, api, refreshUser, logout } = useAuth();
  const { theme, isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Load initial preference from user
    if (user) {
      setEmailNotificationsEnabled(user.email_notifications_enabled !== false);
    }
  }, [user]);

  const handleEmailNotificationToggle = async (enabled) => {
    setUpdating(true);
    try {
      await api.put('/settings/email-notifications', { enabled });
      setEmailNotificationsEnabled(enabled);
      await refreshUser();
      toast.success(enabled ? 'Notifications email activées' : 'Notifications email désactivées');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      // Revert on error
      setEmailNotificationsEnabled(!enabled);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/account');
      toast.success('Votre compte a été supprimé');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setDeleting(false);
      setConfirmDeleteDialogOpen(false);
    }
  };

  const settingsLinks = [
    { 
      icon: User, 
      label: 'Mon profil', 
      description: 'Modifier mes informations et photos',
      to: '/profile'
    },
    { 
      icon: CreditCard, 
      label: 'Mon abonnement', 
      description: user?.is_subscribed ? 'Gérer mon abonnement' : 'Voir les offres',
      to: '/subscription'
    },
    { 
      icon: Shield, 
      label: 'Utilisateurs bloqués', 
      description: 'Gérer les profils bloqués',
      to: '/profile?tab=blocked'
    },
    { 
      icon: FileText, 
      label: 'Politique de confidentialité', 
      description: 'Comment vos données sont protégées',
      to: '/privacy'
    }
  ];

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl gold-gradient-text flex items-center gap-3">
            <Settings className="w-8 h-8 text-gold" />
            Paramètres
          </h1>
          <p className="text-gray-400 mt-2">Gérez votre compte et vos préférences</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Settings Links */}
          <div className="space-y-4">
            <h2 className="font-serif text-lg text-white mb-4">Mon compte</h2>
            
            <div className="glass-card divide-y divide-white/10">
              {settingsLinks.map((item, index) => (
                <Link
                  key={index}
                  to={item.to}
                  className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </Link>
              ))}
              
              {/* Email Notifications Toggle */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Notifications par email</p>
                    <p className="text-sm text-gray-500">
                      Recevoir les emails d'alerte de DiscreetLux
                    </p>
                  </div>
                </div>
                <Switch
                  checked={emailNotificationsEnabled}
                  onCheckedChange={handleEmailNotificationToggle}
                  disabled={updating}
                  className="data-[state=checked]:bg-gold"
                />
              </div>
              
              {/* Theme Toggle */}
              <div className="flex items-center justify-between p-4" data-testid="theme-toggle-container">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    {isDark ? (
                      <Moon className="w-5 h-5 text-gold" />
                    ) : (
                      <Sun className="w-5 h-5 text-gold" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">Apparence</p>
                    <p className="text-sm text-gray-500">
                      {isDark ? 'Mode nuit (sombre)' : 'Mode jour (clair)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{isDark ? 'Nuit' : 'Jour'}</span>
                  <Switch
                    checked={!isDark}
                    onCheckedChange={() => {
                      toggleTheme();
                      toast.success(isDark ? 'Mode jour activé' : 'Mode nuit activé');
                    }}
                    className="data-[state=checked]:bg-gold"
                    data-testid="theme-toggle"
                  />
                  {isDark ? (
                    <Sun className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Moon className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={logout}
              className="w-full glass-card p-4 flex items-center gap-4 text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="logout-btn"
            >
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <LogOut className="w-5 h-5" />
              </div>
              <span className="font-medium">Déconnexion</span>
            </button>
            
            {/* Delete Account Button */}
            <button
              onClick={() => setDeleteDialogOpen(true)}
              className="w-full glass-card p-4 flex items-center gap-4 text-red-500 hover:bg-red-500/10 transition-colors border border-red-500/20"
              data-testid="delete-account-btn"
            >
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="text-left">
                <span className="font-medium block">Supprimer mon compte</span>
                <span className="text-xs text-red-400/70">Action définitive et irréversible</span>
              </div>
            </button>
          </div>

          {/* Blocked Users Section */}
          <div className="space-y-4">
            <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              Profils bloqués
            </h2>
            
            <div className="glass-card p-4">
              <BlockedUsersList />
            </div>
          </div>
        </div>
        
        {/* First Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-dark-paper border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Supprimer votre compte ?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400 space-y-3">
                <p>
                  Êtes-vous sûr de vouloir supprimer définitivement votre compte DiscreetLux ?
                </p>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm space-y-2">
                  <p className="font-semibold">⚠️ Cette action est DÉFINITIVE et IRRÉVERSIBLE :</p>
                  <ul className="list-disc list-inside space-y-1 text-red-400/90">
                    <li>Toutes vos photos et vidéos seront supprimées</li>
                    <li>Vos messages et conversations seront perdus</li>
                    <li>Votre abonnement sera résilié immédiatement sans remboursement</li>
                    <li>Vous ne pourrez pas récupérer votre compte</li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-dark-subtle text-white border-white/10">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setConfirmDeleteDialogOpen(true);
                }}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Je comprends, continuer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Second Confirmation Dialog */}
        <AlertDialog open={confirmDeleteDialogOpen} onOpenChange={setConfirmDeleteDialogOpen}>
          <AlertDialogContent className="bg-dark-paper border-red-500/30">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-500 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Dernière confirmation
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                <p className="mb-4">
                  Vous êtes sur le point de supprimer définitivement votre compte <strong className="text-white">{user?.pseudo}</strong>.
                </p>
                <p className="text-red-400 font-semibold">
                  Cette action ne peut pas être annulée.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-dark-subtle text-white border-white/10"
                disabled={deleting}
              >
                Non, garder mon compte
              </AlertDialogCancel>
              <Button 
                onClick={handleDeleteAccount}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={deleting}
                data-testid="confirm-delete-account-btn"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Suppression...
                  </>
                ) : (
                  'Oui, supprimer mon compte'
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
